import { useState, useRef } from 'react';
import { ChevronDown, Heart, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { HERO, SONG, PLAYLIST } from '@/config/valentineContent';

export default function ValentineHero({ isPlaying, onTogglePlay, volume, onVolumeChange }) {
  const [muted, setMuted] = useState(false);
  const prevVolumeRef = useRef(volume);

  const handleMute = () => {
    if (muted) {
      onVolumeChange?.(prevVolumeRef.current || 70);
      setMuted(false);
    } else {
      prevVolumeRef.current = volume;
      onVolumeChange?.(0);
      setMuted(true);
    }
  };

  return (
    <section id="hero" data-testid="hero-section" className="min-h-screen flex flex-col items-center justify-center text-center px-4">
      <Heart size={28} fill="var(--accent-primary)" strokeWidth={0} className="mb-4 animate-pulse" />
      <h1
        className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        {HERO.heading}
      </h1>
      <p className="font-body text-sm md:text-base mb-6" style={{ color: 'var(--text-secondary)' }}>
        {HERO.subtitle}
      </p>

      {/* Song player */}
      <div className="flex flex-col items-center mb-6">
        <p className="font-body text-[10px] tracking-widest uppercase mb-3" style={{ color: 'var(--accent-light)' }}>
          {PLAYLIST.heading}
        </p>
        <div className="inline-block rounded-2xl p-6 sm:p-8 relative overflow-hidden"
          style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent-glow)', boxShadow: '0 8px 40px var(--accent-glow)' }}>
          <div className="flex justify-center mb-5">
            <div className={`playlist-vinyl-disc ${isPlaying ? 'playlist-vinyl-playing' : ''}`}>
              <div className="playlist-vinyl-grooves">
                <div className="playlist-vinyl-label-center"
                  style={{ background: 'radial-gradient(circle, var(--accent-glow), var(--accent-primary))' }}>
                  <div className="playlist-vinyl-hole" />
                </div>
              </div>
            </div>
          </div>

          <p className="font-heading text-xl mb-0.5" style={{ color: 'var(--text-primary)' }}>{SONG.title}</p>
          <p className="font-body text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>{SONG.artist}</p>

          {isPlaying && (
            <div className="flex items-center justify-center gap-1.5 mb-4 mt-2">
              <div className="flex items-end gap-[2px] h-3">
                <div className="playlist-mini-bar" style={{ backgroundColor: 'var(--accent-primary)', animationDelay: '0ms' }} />
                <div className="playlist-mini-bar" style={{ backgroundColor: 'var(--accent-primary)', animationDelay: '150ms' }} />
                <div className="playlist-mini-bar" style={{ backgroundColor: 'var(--accent-primary)', animationDelay: '300ms' }} />
              </div>
              <span className="font-body text-[10px]" style={{ color: 'var(--accent-primary)' }}>Now Playing</span>
            </div>
          )}
          {!isPlaying && <div className="mb-4 mt-2 h-3" />}

          {/* Play + Volume controls */}
          <div className="flex items-center justify-center gap-4 mb-5">
            <button onClick={handleMute}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--text-secondary)', border: '1px solid var(--accent-glow)', cursor: 'pointer' }}>
              {muted || volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>

            <button onClick={() => onTogglePlay?.()}
              className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
              style={{ background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-light))', boxShadow: '0 4px 20px var(--accent-glow)', color: '#fff', border: 'none', cursor: 'pointer' }}
              data-testid="playlist-play-btn">
              {isPlaying ? <Pause size={22} /> : <Play size={22} />}
            </button>

            <div className="w-8 h-8" /> {/* Spacer for symmetry */}
          </div>

          {/* Volume slider */}
          <div className="flex items-center justify-center gap-2 mb-5">
            <input type="range" min="0" max="100" value={muted ? 0 : volume}
              onChange={(e) => { const v = Number(e.target.value); onVolumeChange?.(v); if (v > 0) setMuted(false); }}
              className="valentine-volume-slider" style={{ width: 120 }} />
          </div>

          <p className="font-body text-xs italic leading-relaxed max-w-xs mx-auto" style={{ color: 'var(--accent-light)' }}>
            "{PLAYLIST.dedication}"
          </p>
        </div>
        <p className="font-body text-[10px] mt-4" style={{ color: 'var(--accent-light)', opacity: 0.6 }}>
          Press play… and come a little closer
        </p>
      </div>

      <div className="mt-10 flex flex-col items-center gap-1">
        <span className="font-body text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>
          {HERO.scrollHint}
        </span>
        <ChevronDown size={18} className="animate-bounce" style={{ color: 'var(--accent-light)' }} />
      </div>
    </section>
  );
}
